import React, { useState, useCallback, useEffect } from 'react';
import { Dropdown, DatePicker, Button, Space } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import {
  PresetId,
  DEFAULT_PRESET,
  getPresetRange,
  toQueryRange,
  formatRangePreview,
  PRESET_OPTIONS,
} from '../../utils/dateRanges';

export interface DateRangeFilterValue {
  startDate: string;
  endDate: string;
  presetId: PresetId;
}

export interface DateRangeFilterProps {
  value: DateRangeFilterValue;
  onChange: (value: DateRangeFilterValue) => void;
  loading?: boolean;
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  value,
  onChange,
  loading = false,
}) => {
  const [customFrom, setCustomFrom] = useState<Dayjs | null>(() =>
    value.presetId === 'custom' ? dayjs(value.startDate) : null
  );
  const [customTo, setCustomTo] = useState<Dayjs | null>(() =>
    value.presetId === 'custom' ? dayjs(value.endDate) : null
  );
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (value.presetId === 'custom') {
      setCustomFrom(dayjs(value.startDate));
      setCustomTo(dayjs(value.endDate));
    } else {
      setCustomFrom(null);
      setCustomTo(null);
    }
  }, [value.presetId, value.startDate, value.endDate]);

  const handlePresetSelect = useCallback(
    (presetId: PresetId) => {
      if (presetId === 'custom') {
        const range = getPresetRange(DEFAULT_PRESET);
        setCustomFrom(dayjs(range.from));
        setCustomTo(dayjs(range.to));
        return;
      }
      const range = getPresetRange(presetId);
      const query = toQueryRange(range.from, range.to);
      onChange({
        startDate: query.startDate,
        endDate: query.endDate,
        presetId,
      });
      setOpen(false);
    },
    [onChange]
  );

  const handleCustomApply = useCallback(() => {
    if (!customFrom || !customTo) return;
    if (customFrom.isAfter(customTo)) return;
    const query = toQueryRange(customFrom.toDate(), customTo.toDate());
    onChange({
      startDate: query.startDate,
      endDate: query.endDate,
      presetId: 'custom',
    });
    setOpen(false);
  }, [customFrom, customTo, onChange]);

  const handleReset = useCallback(() => {
    const range = getPresetRange(DEFAULT_PRESET);
    const query = toQueryRange(range.from, range.to);
    onChange({
      startDate: query.startDate,
      endDate: query.endDate,
      presetId: DEFAULT_PRESET,
    });
    setCustomFrom(null);
    setCustomTo(null);
    setOpen(false);
  }, [onChange]);

  const previewText = formatRangePreview(
    dayjs(value.startDate).toDate(),
    dayjs(value.endDate).toDate()
  );

  const isValidCustom = customFrom && customTo && !customFrom.isAfter(customTo);

  const dropdownContent = (
    <div
      className="admin-date-filter-dropdown"
      style={{
        background: 'var(--ant-color-bg-elevated, #fff)',
        borderRadius: 12,
        boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
        padding: 16,
        minWidth: 320,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: 0.02,
          marginBottom: 12,
        }}
      >
        Presets
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 16,
        }}
      >
        {PRESET_OPTIONS.map(opt => (
          <Button
            key={opt.id}
            type={value.presetId === opt.id ? 'primary' : 'default'}
            size="small"
            onClick={() => handlePresetSelect(opt.id)}
            style={{
              borderRadius: 8,
              ...(value.presetId === opt.id ? {} : { borderColor: '#e5e7eb' }),
            }}
          >
            {opt.label}
          </Button>
        ))}
      </div>

      <div
        style={{
          borderTop: '1px solid #f0f0f0',
          paddingTop: 16,
          marginTop: 8,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: 0.02,
            marginBottom: 12,
          }}
        >
          Tùy chọn khoảng ngày
        </div>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space wrap size="small">
            <DatePicker
              placeholder="Từ ngày"
              value={customFrom}
              onChange={d => setCustomFrom(d)}
              format="DD/MM/YYYY"
              size="middle"
              style={{ width: 140 }}
            />
            <DatePicker
              placeholder="Đến ngày"
              value={customTo}
              onChange={d => setCustomTo(d)}
              format="DD/MM/YYYY"
              size="middle"
              style={{ width: 140 }}
            />
          </Space>
          <Space>
            <Button
              type="primary"
              size="small"
              onClick={handleCustomApply}
              disabled={!isValidCustom}
              style={{ borderRadius: 8 }}
            >
              Áp dụng
            </Button>
          </Space>
        </Space>
      </div>
    </div>
  );

  return (
    <div className="admin-date-range-filter">
      <Dropdown
        open={open}
        onOpenChange={setOpen}
        trigger={['click']}
        dropdownRender={() => dropdownContent}
        placement="bottomRight"
      >
        <Button
          size="middle"
          icon={<CalendarOutlined />}
          loading={loading}
          style={{
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            borderColor: '#e5e7eb',
            background: '#fff',
          }}
        >
          {previewText}
          <span style={{ fontSize: 10, opacity: 0.7 }}>▾</span>
        </Button>
      </Dropdown>
    </div>
  );
};

export default DateRangeFilter;
